import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Store, Loader2, CheckCircle2, Locate, Phone, IndianRupee, Sparkles, ShieldCheck, MessageCircle } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import LocationPicker from "@/components/LocationPicker";

const initialForm = {
  business_name: "",
  category: "store",
  phone: "",
  city: "",
  address: "",
  lat: null,
  lng: null,
  description: "",
  price: "",
  price_unit: "fixed",
  owner_display_name: "",
  whatsapp_opt_in: true,   // pre-checked because it's genuinely useful for the owner
  website_url: "",       // HONEYPOT — hidden field. Bots fill it, humans don't.
};

export default function ListMyBusiness() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [success, setSuccess] = useState(null);        // { listing_id, message }

  const update = (patch) => setForm(f => ({ ...f, ...patch }));

  // Try browser geolocation on mount so the form pre-fills wherever possible.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => update({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},                                        // silent — user can pin manually
      { enableHighAccuracy: false, timeout: 4000 },
    // Silent — user can pin manually
    );
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location not supported by this browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        update({ lat: latitude, lng: longitude });
        // Reverse-geocode via free Nominatim to prefill city/address.
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`, {
            headers: { "Accept-Language": "en" },
          });
          const j = await r.json();
          const addr = j.address || {};
          update({
            city: addr.city || addr.town || addr.village || addr.suburb || form.city,
            address: j.display_name || form.address,
          });
        } catch (_) { /* silent */ }
        toast.success("Location captured");
        setLocating(false);
      },
      (err) => {
        toast.error(`Couldn't get your location: ${err.message}`);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const geocodeAddress = async () => {
    if (!form.address && !form.city) {
      toast.error("Type an address or city first");
      return;
    }
    setGeocoding(true);
    try {
      const q = `${form.address}, ${form.city}`.trim();
      const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1`, {
        headers: { "Accept-Language": "en" },
      });
      const j = await r.json();
      if (j?.length) {
        update({ lat: parseFloat(j[0].lat), lng: parseFloat(j[0].lon) });
        toast.success("Address found on map");
      } else {
        toast.error("Couldn't find that address. Try adding city + landmark.");
      }
    } catch (e) {
      toast.error("Geocoding failed");
    } finally {
      setGeocoding(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim()) return toast.error("Please enter your business name");
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 10) return toast.error("A valid 10+ digit phone is required for verification");
    if (form.lat == null || form.lng == null) return toast.error("Please pin your location (tap 'Use my location' or type your address)");

    setSubmitting(true);
    try {
      const r = await api.post("/public/list-business", {
        ...form,
        price: parseFloat(form.price || 0) || 0,
      });
      setSuccess(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 rounded-3xl p-10 text-center" data-testid="list-business-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-emerald-700" />
          </div>
          <h1 className="heading text-3xl font-extrabold text-stone-900 mb-2">You&apos;re on the list!</h1>
          <p className="text-stone-600 leading-relaxed mb-3">{success.message}</p>
          {form.whatsapp_opt_in && (
            <div className="inline-flex items-center gap-2 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 mb-4" data-testid="list-business-wa-confirmation">
              <MessageCircle size={12} /> We&apos;ll WhatsApp your QR sticker as soon as we verify you.
            </div>
          )}
          <div className="text-xs text-stone-400 font-mono mb-6">Ref: {success.listing_id}</div>
          <div className="flex gap-2 justify-center flex-wrap">
            <Button
              onClick={() => { setSuccess(null); setForm(initialForm); }}
              variant="outline"
              className="rounded-full"
              data-testid="list-business-add-another"
            >
              List another business
            </Button>
            <a href="/">
              <Button className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="list-business-go-home">
                Explore Find2Near
              </Button>
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-8">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[#2D5A27] bg-[#2D5A27]/10 px-3 py-1 rounded-full mb-3">
          <Sparkles size={12} /> Free forever · No signup required
        </div>
        <h1 className="heading text-4xl md:text-5xl font-extrabold text-stone-900 leading-tight">
          List your business on Find2Near
        </h1>
        <p className="text-stone-600 mt-2 max-w-xl">
          Street vendor, small shop, home tutor, roadside dhaba — everyone gets discovered here.
          Fill this in under a minute. Our team verifies each listing within 24 hours.
        </p>
      </motion.div>

      {/* Form */}
      <form onSubmit={submit} className="bg-white border border-stone-200 rounded-3xl p-5 md:p-7 space-y-5" data-testid="list-business-form">
        {/* Honeypot field — visible only to bots */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website_url}
          onChange={e => update({ website_url: e.target.value })}
          style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
          aria-hidden="true"
        />

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
              <Store size={12} /> Business name
            </label>
            <Input
              value={form.business_name}
              onChange={e => update({ business_name: e.target.value })}
              placeholder="e.g. Sharma Tea Stall, Radhika Beauty Parlour"
              className="mt-1.5 rounded-xl h-11"
              maxLength={80}
              required
              data-testid="list-business-name"
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">What do you do?</label>
            <Select value={form.category} onValueChange={v => update({ category: v })}>
              <SelectTrigger className="mt-1.5 rounded-xl h-11" data-testid="list-business-category"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {CATEGORIES.filter(c => !["yojna", "commodity", "antique"].includes(c.key)).map(c => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
              <Phone size={12} /> Your phone
            </label>
            <Input
              value={form.phone}
              onChange={e => update({ phone: e.target.value })}
              placeholder="10-digit mobile"
              type="tel"
              className="mt-1.5 rounded-xl h-11"
              required
              data-testid="list-business-phone"
            />
            <div className="text-[11px] text-stone-500 mt-1">Only for verification. Won&apos;t be publicly shown until you approve.</div>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
              <MapPin size={12} /> Where are you located?
            </label>
            <div className="grid md:grid-cols-2 gap-2 mt-1.5">
              <Input
                value={form.city}
                onChange={e => update({ city: e.target.value })}
                placeholder="City (e.g. Delhi)"
                className="rounded-xl h-11"
                data-testid="list-business-city"
              />
              <Input
                value={form.address}
                onChange={e => update({ address: e.target.value })}
                placeholder="Street / area / landmark"
                className="rounded-xl h-11"
                data-testid="list-business-address"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Button type="button" onClick={useMyLocation} disabled={locating} variant="outline" className="rounded-full text-xs h-8" data-testid="list-business-use-location">
                {locating ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Locate size={12} className="mr-1.5" />}
                Use my location
              </Button>
              <Button type="button" onClick={geocodeAddress} disabled={geocoding} variant="outline" className="rounded-full text-xs h-8" data-testid="list-business-geocode">
                {geocoding ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <MapPin size={12} className="mr-1.5" />}
                Find address on map
              </Button>
              {form.lat != null && form.lng != null && (
                <span className="text-[11px] text-emerald-700 font-bold inline-flex items-center gap-1" data-testid="list-business-pinned">
                  <CheckCircle2 size={12} /> Pinned: {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
                </span>
              )}
            </div>

            {/* Phase 3 · worldwide dual-path picker. Preserves the legacy
                 buttons above for muscle memory; this panel adds the
                 GPS-safe "🗺️ Choose on map" + search flow so a user can
                 tag a business anywhere on Earth. */}
            <div className="mt-3 rounded-2xl bg-stone-50 border border-stone-200 p-3">
              <div className="text-[11px] font-black uppercase tracking-widest text-stone-500 mb-2">
                Pin exact location (worldwide)
              </div>
              <LocationPicker
                value={form.lat != null && form.lng != null
                         ? { lat: form.lat, lng: form.lng, city: form.city, source: "manual" }
                         : null}
                onChange={(loc) => update({
                  lat:  Number(Number(loc.lat).toFixed(6)),
                  lng:  Number(Number(loc.lng).toFixed(6)),
                  city: loc.city || form.city,
                })}
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Tell customers about your business</label>
            <Textarea
              value={form.description}
              onChange={e => update({ description: e.target.value })}
              placeholder="What you offer, timings, specialities, popular items…"
              className="mt-1.5 rounded-xl min-h-[80px]"
              maxLength={500}
              data-testid="list-business-description"
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
              <IndianRupee size={12} /> Typical starting price
            </label>
            <div className="flex gap-2 mt-1.5">
              <Input
                value={form.price}
                onChange={e => update({ price: e.target.value })}
                placeholder="0"
                type="number"
                className="rounded-xl h-11"
                data-testid="list-business-price"
              />
              <Select value={form.price_unit} onValueChange={v => update({ price_unit: v })}>
                <SelectTrigger className="rounded-xl h-11 w-32" data-testid="list-business-price-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="hour">/hour</SelectItem>
                  <SelectItem value="day">/day</SelectItem>
                  <SelectItem value="month">/month</SelectItem>
                  <SelectItem value="plate">/plate</SelectItem>
                  <SelectItem value="per_piece">/piece</SelectItem>
                  <SelectItem value="per_pair">/pair</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Your name (optional)</label>
            <Input
              value={form.owner_display_name}
              onChange={e => update({ owner_display_name: e.target.value })}
              placeholder="e.g. Ravi Sharma"
              className="mt-1.5 rounded-xl h-11"
              data-testid="list-business-owner-name"
            />
          </div>
        </div>

        {/* WhatsApp opt-in — sends the printable QR sticker once admin approves */}
        <label
          htmlFor="wa-opt-in"
          className="flex items-start gap-3 p-3 rounded-2xl border-2 border-emerald-100 bg-emerald-50/50 hover:border-emerald-200 cursor-pointer transition-colors"
          data-testid="list-business-wa-optin-wrapper"
        >
          <input
            id="wa-opt-in"
            type="checkbox"
            checked={form.whatsapp_opt_in}
            onChange={e => update({ whatsapp_opt_in: e.target.checked })}
            className="mt-1 h-4 w-4 accent-emerald-600"
            data-testid="list-business-wa-optin"
          />
          <div className="flex-1">
            <div className="text-sm font-bold text-stone-900 flex items-center gap-1.5">
              <MessageCircle size={14} className="text-emerald-700" />
              WhatsApp me the printable QR sticker once approved
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              Once we verify your business, we&apos;ll WhatsApp you a downloadable QR sticker. Print + paste it on your storefront — customers scan it to view + review you.
            </div>
          </div>
        </label>

        <div className="pt-2 flex flex-wrap items-center justify-between gap-3 border-t border-stone-100">
          <div className="text-[11px] text-stone-500 max-w-md leading-relaxed inline-flex items-start gap-2">
            <ShieldCheck size={14} className="text-[#2D5A27] mt-0.5 shrink-0" />
            <span>Your listing enters a moderation queue. We verify each business by calling the phone number before it appears in search results. This protects buyers from fake listings.</span>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] px-6 h-11 font-bold"
            data-testid="list-business-submit"
          >
            {submitting ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Submitting…</> : "Submit for review"}
          </Button>
        </div>
      </form>
    </div>
  );
}
